import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, MessageCircle, Phone, Copy, Check, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import FloatingActions from '@/components/site/FloatingActions';
import Watermark from '@/components/site/Watermark';
import VirtualTryOn from '@/components/site/VirtualTryOn';
import { getProductBySlug, useCatalog } from '@/hooks/useCatalog';
import useSEO from '@/hooks/useSEO';
import { productImageUrl } from '@/lib/imageUrl';

const WHATSAPP_NUMBER = '19843779835';
const PHONE_TEL = '+19843779835';

/**
 * Reusable Product + BreadcrumbList JSON-LD builder.
 * Only emits fields we actually have — never invents price, availability, ratings, or reviews.
 */
function buildProductJsonLd(product, categoryLabel, imageUrl, canonicalUrl) {
  const price = (product.price || '').trim();
  const priceNum = price ? Number((price.match(/[\d.]+/) || [])[0]) : undefined;
  const currency = /usd|\$/i.test(price) || !priceNum ? 'USD' : 'USD';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.label,
    description: product.sub || product.description || `${product.label} — ${categoryLabel} at Trendy Closet, Morrisville NC.`,
    sku: product.slug,
    productID: product.slug,
    brand: { '@type': 'Brand', name: 'Trendy Closet' },
    category: categoryLabel || product.category,
    url: canonicalUrl,
    image: imageUrl ? [imageUrl] : undefined,
  };

  // Emit an `offers` block whenever we have a numeric price OR the product
  // is sold — the latter case lets Google see the OutOfStock availability
  // even if the boutique keeps prices as "On request".
  if (priceNum && Number.isFinite(priceNum)) {
    productSchema.offers = {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: currency,
      price: priceNum.toFixed(2),
      availability: product.sold
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    };
  } else if (product.sold) {
    productSchema.offers = {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: 'USD',
      availability: 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    };
  }

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://trendyclosetusa.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel || product.category, item: `https://trendyclosetusa.com/category/${product.category}` },
      { '@type': 'ListItem', position: 3, name: product.label, item: canonicalUrl },
    ],
  };

  return [productSchema, breadcrumbs];
}

const Meta = ({ k, v, rich = false }) => {
  if (!v) return null;
  return (
    <div className="grid grid-cols-3 gap-4 py-4 border-b border-brandborder/70">
      <div className="overline col-span-1">{k}</div>
      <div className="text-sm text-charcoal/80 col-span-2 leading-relaxed">
        {rich ? <RichText value={v} /> : v}
      </div>
    </div>
  );
};

/**
 * Renders a string with newlines and simple bullet points.
 * - Lines starting with "-", "*", "•" are rendered as list items (consecutive bullets grouped in a <ul>).
 * - Other non-empty lines render as paragraphs.
 */
const RichText = ({ value }) => {
  const lines = String(value).split(/\r?\n/);
  const blocks = [];
  let currentList = null;
  const flushList = () => {
    if (currentList && currentList.length) {
      blocks.push({ type: 'ul', items: currentList });
    }
    currentList = null;
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flushList(); return; }
    const bulletMatch = line.match(/^\s*[-*•]\s+(.*)/);
    if (bulletMatch) {
      if (!currentList) currentList = [];
      currentList.push(bulletMatch[1]);
    } else {
      flushList();
      blocks.push({ type: 'p', text: line });
    }
  });
  flushList();
  return (
    <>
      {blocks.map((b, i) => b.type === 'ul' ? (
        <ul key={i} className="list-disc pl-5 space-y-1 my-1">
          {b.items.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      ) : (
        <p key={i} className="my-1">{b.text}</p>
      ))}
    </>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { categories } = useCatalog();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let on = true;
    setLoading(true);
    getProductBySlug(slug)
      .then((p) => { if (on) setProduct(p); })
      .catch(() => { if (on) setProduct(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [slug]);

  const categoryLabel = product ? (categories.find((c) => c.key === product.category)?.label || product.category) : '';
  const seoImage = productImageUrl(product);
  const SITE_ORIGIN = 'https://trendyclosetusa.com';
  const canonicalUrl = product ? `${SITE_ORIGIN}/product/${product.slug}` : undefined;
  const productJsonLd = product ? buildProductJsonLd(product, categoryLabel, seoImage, canonicalUrl) : undefined;
  useSEO({
    title: product
      ? `${product.label} · ${categoryLabel} — Trendy Closet Morrisville NC`
      : 'Product not found — Trendy Closet',
    description: product
      ? (product.sub || `${product.label} — ${categoryLabel} at Trendy Closet, Morrisville NC.`) + ' Enquire on WhatsApp or call 984-377-9835. Serving Cary, Raleigh, Durham.'
      : 'This piece is no longer in the closet. Explore the rest of the collection at Trendy Closet, Morrisville NC.',
    image: seoImage || undefined,
    canonical: canonicalUrl,
    type: 'product',
    jsonLd: productJsonLd,
    // If the fetch resolved but returned null, this is a 404 — tell crawlers.
    noindex: !loading && !product,
  });

  if (loading) {
    return (
      <main className="min-h-screen bg-ivory text-charcoal">
        <Navbar />
        <section className="container-luxe py-40 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></section>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-screen bg-ivory text-charcoal">
        <Navbar />
        <section className="container-luxe py-40 text-center">
          <span className="overline">Not Found</span>
          <h1 className="font-display text-5xl mt-4">This piece is no longer in the closet.</h1>
          <p className="mt-4 text-charcoal/70">It may have found a new home — please explore the rest of the collection.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-burgundy text-ivory text-xs uppercase tracking-[0.22em] hover:bg-maroon transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const productUrl = typeof window !== 'undefined' ? window.location.href : '';
  const whatsappText = `${productUrl}

Hi Trendy Closet! ✨ I'd love to enquire about *${product.label}* (${categoryLabel}).

Could you please share availability, sizing and pricing?

Thank you!`;
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappText)}`;

  const copyProductLink = async () => {
    try {
      await navigator.clipboard.writeText(productUrl);
      setCopied(true);
      toast.success('Product link copied to clipboard');
      setTimeout(() => setCopied(false), 2200);
    } catch { toast.error('Could not copy — please copy manually'); }
  };

  const openFullImage = (e) => {
    e.preventDefault();
    if (product.image_seo_name && seoImage) {
      window.open(seoImage, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!product.image) return;
    const isRemote = /^https?:\/\//i.test(product.image);
    if (isRemote) {
      window.open(product.image, '_blank', 'noopener,noreferrer');
      return;
    }
    // base64 → blob URL so the browser can open it in a new tab
    try {
      const [meta, b64] = product.image.split(',');
      const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later so the tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Could not open image');
    }
  };

  return (
    <main data-testid="product-page" className="min-h-screen bg-ivory text-charcoal">
      <Navbar />
      <section className="pt-28 lg:pt-32 pb-20 lg:pb-28">
        <div className="container-luxe">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/55 mb-10">
            <Link to="/" className="hover:text-maroon" data-testid="breadcrumb-home">Home</Link><span>/</span>
            <Link to={`/category/${product.category}`} className="hover:text-maroon" data-testid="breadcrumb-category">{categoryLabel}</Link><span>/</span>
            <span className="text-charcoal" data-testid="breadcrumb-current">{product.label}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7">
              <div className="relative overflow-hidden shadow-card border border-brandborder/70">
                <img src={seoImage} alt={`${product.label} — ${categoryLabel} at Trendy Closet, Morrisville NC`} className="w-full h-auto object-cover aspect-[4/5]" data-testid="product-image" fetchpriority="high" decoding="async" />
                <Watermark size="lg" />
                {product.sold && (
                  <div className="absolute inset-0 pointer-events-none z-20" data-testid="product-sold-overlay" aria-label="Sold">
                    <div className="absolute inset-0 bg-charcoal/30" />
                    <div className="absolute top-8 -right-20 w-72 rotate-45 bg-burgundy text-ivory text-center py-3 text-sm uppercase tracking-[0.4em] shadow-xl font-display">
                      Sold
                    </div>
                  </div>
                )}
                <div className="absolute inset-3 border border-gold/40 pointer-events-none" />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={openFullImage}
                  data-testid="product-image-open"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-brandborder hover:border-maroon hover:text-maroon text-xs uppercase tracking-[0.18em] transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Open full image
                </button>
                <button onClick={copyProductLink} data-testid="product-image-copy" className="inline-flex items-center gap-2 px-5 py-3 border border-brandborder hover:border-maroon hover:text-maroon text-xs uppercase tracking-[0.18em] transition-colors">
                  {copied ? <Check className="w-4 h-4 text-maroon" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy product link'}
                </button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="lg:col-span-5">
              <span className="overline">{categoryLabel}</span>
              <h1 className="font-display text-4xl lg:text-5xl text-charcoal mt-3 leading-[1.05] tracking-tight">{product.label}</h1>
              <div className="mt-4 flex items-center gap-2">
                {[...Array(5)].map((_, k) => (<Star key={k} className="w-3.5 h-3.5 fill-gold text-gold" />))}
              <a
                href="https://www.google.com/search?q=Trendy+Closet+Indian+Clothing+Reviews"
                target="_blank" rel="noreferrer"
                className="text-xs text-charcoal/55 ml-1 hover:text-maroon transition-colors underline-offset-4 hover:underline"
              >
                5.0 · 40+ Google reviews
              </a>
              </div>
              {product.sub && <p className="mt-5 text-charcoal/75 leading-relaxed">{product.sub}</p>}

              <div className="mt-8 flex items-baseline gap-3">
                <span className="overline">Price</span>
                <span className="font-display text-2xl text-maroon">{product.price || 'On request'}</span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="product-whatsapp" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#25D366] text-white text-xs uppercase tracking-[0.22em] hover:bg-[#1ebe5b] transition-colors duration-500 shadow-card"><MessageCircle className="w-4 h-4" /> Enquire on WhatsApp</a>
                <a href={`tel:${PHONE_TEL}`} data-testid="product-call" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-burgundy text-burgundy hover:bg-burgundy hover:text-ivory text-xs uppercase tracking-[0.22em] transition-all duration-500"><Phone className="w-4 h-4" /> Call Trendy Closet</a>
              </div>
              <div className="mt-3">
                <VirtualTryOn product={product} variant="detail" />
              </div>
              <p className="mt-3 text-xs text-charcoal/55">Tapping WhatsApp opens a chat pre-filled with a link to this exact piece so the owner knows precisely which outfit you mean.</p>

              {product.tags?.length > 0 && (
                <div className="hidden" data-testid="product-tags-hidden">
                  {/* Tags are managed internally for search but hidden from public product page */}
                </div>
              )}

              <div className="mt-10 border-t border-brandborder/70">
                <Meta k="Highlights" v={product.fabric} rich />
                <Meta k="Colour" v={product.color} />
                <Meta k="Occasion" v={product.occasion} />
                <Meta k="Inclusions" v={product.inclusions} />
                <Meta k="Sizing" v={product.sizes} />
                <Meta k="Care" v={product.care} />
              </div>

              {product.description && (
                <p className="mt-8 text-charcoal/75 leading-relaxed">{product.description}</p>
              )}

              <div
                data-testid="product-disclaimer"
                className="mt-10 border border-gold/50 bg-blush/40 p-6 lg:p-7"
                role="note"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-4 bg-gold" aria-hidden="true" />
                  <span className="overline text-maroon">Please note</span>
                </div>
                <p className="text-sm leading-relaxed text-charcoal/80">
                  Images, colours, styling, and product descriptions on this website are provided for reference only.
                  Actual products may vary due to lighting, photography, screen settings, and design updates. We recommend
                  visiting our in-house showroom in <strong className="text-charcoal">Morrisville, NC</strong> to view the
                  latest collection before making your purchase.
                </p>
              </div>

              <Link to="/#gallery" data-testid="product-back" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-charcoal/70 hover:text-maroon transition-colors"><ArrowLeft className="w-4 h-4" /> Back to gallery</Link>
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
      <FloatingActions />
    </main>
  );
}
